import React, { useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  OwcHeaderRow,
  OwcTypography,
  OwcButton,
  OwcIcon,
  OwcIconButton,
  OwcDivider,
  OwcAvatar,
  OwcLogo,
  OwcMenu,
  OwcListItem,
  OwcHyperlink,
} from "@one/react";
import { OwcFooter } from "@one/react/Components/OwcFooter";
import "../assets/logo.css";

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { authState, logout } = useAuth();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLOwcHeaderRowElement>(null);
  const footerRef = useRef<HTMLOwcFooterElement>(null);
  const [contactInfoOpen, setContactInfoOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handlePrivacyPolicy = () => {
    navigate("/privacy-policy");
  };

  const toggleContactInfo = () => {
    setContactInfoOpen(!contactInfoOpen);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100%", /* Prevent overflow beyond viewport width */
        overflowX: "hidden", /* Hide horizontal overflow */
        position: "relative", /* Establish positioning context */
      }}
    >
      {/* Header */}
      <OwcHeaderRow ref={headerRef} variant="primary">
        <div
          className="owc-flex owc-align-center"
          style={{
            width: "100%",
            padding: "0 24px",
            maxWidth: "1440px",
            margin: "0 auto",
          }}
        >
          {/* Left side navigation */}
          <div className="owc-flex owc-align-center owc-gap-md">
            <div className="owc-mr-2">
              <OwcLogo name="navify_light_green" size="mini"></OwcLogo>
            </div>

            <OwcTypography className="owc-mr-2">
              Gestational Diabetes Management
            </OwcTypography>

            <OwcDivider direction="vertical" className="owc-mr-2" />

            <OwcTypography style={{ fontWeight: "bold" }}>
              Patient Worklist
            </OwcTypography>
          </div>

          {/* Spacer to push right elements */}
          <div className="owc-flex-grow-1"></div>

          {/* Right side navigation */}
          {authState.isAuthenticated && (
            <div className="owc-flex owc-align-center owc-gap-md">
              <OwcIconButton
                family="outlined"
                icon="circle_info"
                aria-label="Information"
              ></OwcIconButton>

              <OwcIconButton
                family="outlined"
                icon="circle_help"
                aria-label="Help"
              ></OwcIconButton>

              <OwcDivider direction="vertical" />

              <div className="owc-relative" id="menu-trigger-area">
                <OwcAvatar
                  onClick={toggleContactInfo}
                  style={{ cursor: "pointer" }}
                  aria-label="Contact Information"
                >
                  {authState.user?.firstName && authState.user?.lastName
                    ? `${authState.user?.firstName[0]}${authState.user?.lastName[0]}`
                    : authState.user?.email?.substring(0, 2).toUpperCase() ||
                      "U"}
                </OwcAvatar>

                <OwcMenu
                  anchor="menu-trigger-area"
                  onClickOutside={() => setContactInfoOpen(false)}
                  visible={contactInfoOpen}
                  position="bottom"
                  style={{ width: "250px" }}
                >
                  {authState.user && (
                    <OwcListItem
                      style={{ cursor: "default", pointerEvents: "none" }}
                    >
                      <div style={{ padding: "8px 8px 4px 8px" }}>
                        <OwcTypography>
                          {authState.user.firstName && authState.user.lastName
                            ? `${authState.user.firstName} ${authState.user.lastName}`
                            : "User"}
                        </OwcTypography>
                      </div>
                      <div
                        style={{ padding: "0px 8px 8px 8px", fontSize: "14px" }}
                      >
                        {authState.user.email || "No email"}
                      </div>
                    </OwcListItem>
                  )}

                  <OwcDivider className="owc-my-1" />

                  <OwcListItem onClick={handlePrivacyPolicy}>
                    <span
                      style={{ verticalAlign: "middle", paddingRight: "8px" }}
                    >
                      <OwcIcon name="security" family="outlined"></OwcIcon>
                    </span>
                    <span style={{ lineHeight: "2.275rem" }}>
                      Privacy Policy
                    </span>
                  </OwcListItem>

                  <OwcDivider className="owc-my-1" />

                  <div
                    style={{
                      textAlign: "right",
                      padding: "8px 8px",
                    }}
                  >
                    <OwcButton
                      style={{
                        margin: "4px",
                        display: "inline-block",
                      }}
                      variant="secondary"
                      size="medium"
                      onClick={handleLogout}
                    >
                      Sign Out
                    </OwcButton>
                  </div>
                </OwcMenu>
              </div>

              <OwcLogo name="roche_blue"></OwcLogo>
            </div>
          )}
        </div>
      </OwcHeaderRow>

      {/* Main content - this will push the footer down */}
      <main
        style={{
          flex: "1 0 auto",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          overflowX: "hidden", /* Prevent horizontal scrolling at container level */
          position: "relative",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            maxWidth: "1440px",
            margin: "0 auto",
            width: "100%",
            boxSizing: "border-box", /* Ensure padding is included in width */
            overflowX: "hidden",
          }}
        >
          {children}
        </div>
      </main>

      {/* Footer using the recommended structure with variant and slots */}
      <OwcFooter
        ref={footerRef}
        fixBottom
        variant="default"
        style={{
          width: "100%",
          maxWidth: "100%", /* Prevent overflow */
          boxSizing: "border-box", /* Ensure padding is included in width */
          backgroundColor: "white",
          borderTop: "1px solid var(--one-color-gray-200)",
        }}
      >
        <div
          slot="top"
          style={{
            display: "flex",
            gap: "16px",
            maxWidth: "1440px",
            margin: "0 auto",
            width: "100%",
            padding: "0 24px",
          }}
        >
          <OwcIcon name="circle_info" family="outlined"></OwcIcon>
          <OwcIcon name="security" family="outlined"></OwcIcon>
          <OwcIcon name="circle_help" family="outlined"></OwcIcon>
          <OwcIcon name="mail" family="outlined"></OwcIcon>
        </div>
        <div
          slot="bottom"
          style={{
            display: "flex",
            gap: "16px",
            maxWidth: "1440px",
            margin: "0 auto",
            width: "100%",
            padding: "0 24px",
            alignItems: "center",
          }}
        >
          <OwcHyperlink href="#" onClick={() => navigate("/privacy-policy")}>
            Privacy Policy
          </OwcHyperlink>
          <OwcHyperlink href="#">Legal Statement</OwcHyperlink>
          <OwcHyperlink href="mailto:support@example.com">
            Contact Us
          </OwcHyperlink>
          <span style={{ marginLeft: "auto" }}>
            © {new Date().getFullYear()} Patient Management System | v1.2.0
          </span>
        </div>
      </OwcFooter>
    </div>
  );
};

export default MainLayout;
